import { useContext } from "react";
import { QueryClient, QueryClientContext, useQuery } from "@tanstack/react-query";
import { useLanguage } from "@/components/LanguageProvider";
import { loadStaticLanguage } from "@/lib/staticTranslations";
import type { Translations, Language } from "@/lib/translations";
import { interpolate, isMsg, type Msg, type MsgVars } from "@shared/i18n";
import { loadServerTextCatalogues } from "@shared/serverText";

/**
 * The translation accessor. Every string is called with its English beside
 * its key:
 *
 *   t("contactsEmptyTitle", "No contacts yet")
 *   t("contactsCount", "{count} contacts", { count })
 *   t(msg("tasksTabAll", "All"))           // a descriptor from shared/i18n
 *
 * A key is looked up in the language's registry rows first, then in that
 * language's bundled table (lib/staticTranslations.ts), then the English
 * given here. So English needs no dictionary at all, and the legacy one
 * (lib/translations.ts) is not on the first load.
 *
 * It used to be property access as well (`t.loginTitle`), which needed the
 * whole English table in the entry chunk to answer. Those reads were all
 * rewritten as calls with the same English; the type no longer offers them,
 * so one cannot come back unnoticed.
 */
export type TranslationAccessor = {
  (key: string, fallback?: string, vars?: MsgVars): string;
  (m: Msg, vars?: MsgVars): string;
};


type Inputs = {
  /** Part of the cache key only: a new accessor once server text can be read. */
  serverTextReady?: boolean;
  language: string;
  db: Record<string, string> | undefined;
  staticLang: Partial<Translations> | undefined;
};

/*
  One accessor per (language, registry rows, bundled chunk), shared by every
  component, so a table whose rows each call useTranslation does not build
  one per row. React Query hands every observer the same `data` reference,
  so identity is the right key. A few entries, because a language switch
  briefly has components on both sides of it.
*/
const cache: Array<Inputs & { fn: TranslationAccessor }> = [];

function build({ language, db, staticLang }: Inputs): TranslationAccessor {
  const rows = language === "en" ? undefined : db;
  const table = (language === "en" ? undefined : staticLang) as Record<string, unknown> | undefined;
  const lookup = (key: string): string | undefined => {
    const fromDb = rows?.[key];
    if (typeof fromDb === "string" && fromDb) return fromDb;
    const fromTable = table?.[key];
    return typeof fromTable === "string" ? fromTable : undefined;
  };

  const fn = ((keyOrMsg: string | Msg, a?: string | MsgVars, b?: MsgVars): string => {
    let key: string;
    let fallback: string | undefined;
    let vars: MsgVars | undefined;
    if (isMsg(keyOrMsg)) {
      key = keyOrMsg.key;
      fallback = keyOrMsg.en;
      vars = a as MsgVars | undefined;
    } else {
      key = keyOrMsg;
      fallback = a as string | undefined;
      vars = b;
    }
    return interpolate(lookup(key) ?? fallback ?? key, vars);
  }) as TranslationAccessor;

  return fn;
}

function accessorFor(inputs: Inputs): TranslationAccessor {
  const hit = cache.find((c) => c.language === inputs.language && c.db === inputs.db && c.staticLang === inputs.staticLang && c.serverTextReady === inputs.serverTextReady);
  if (hit) return hit.fn;
  const fn = build(inputs);
  cache.unshift({ ...inputs, fn });
  cache.length = Math.min(cache.length, 4);
  return fn;
}

/*
  The primitives (StatusChip, LoadError, the skeletons) call this hook, and
  they render in places with no QueryClientProvider above them — unit tests,
  and anything mounted outside the app root. useQuery throws there, so such a
  caller gets a private client: off English it still fetches; in English it
  never queries at all.
*/
let fallbackClient: QueryClient | undefined;
function useClient(): QueryClient {
  const provided = useContext(QueryClientContext);
  return provided ?? (fallbackClient ??= new QueryClient());
}

export function useTranslation(): TranslationAccessor {
  const { language } = useLanguage();
  const client = useClient();

  const { data: dbTranslations } = useQuery<Record<string, string>>({
    queryKey: ["/api/translations/language", language],
    queryFn: async () => {
      if (language === "en") return {};
      const response = await fetch(`/api/translations/language/${language}`, {
        credentials: "include",
      });
      if (!response.ok) return {};
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    enabled: language !== "en",
  }, client);

  // The bundled tables live in lazy chunks; this resolves the chunk once and
  // caches it forever. Until it resolves, the registry rows and the English
  // at each call site render.
  const { data: staticLangData } = useQuery<Partial<Translations>>({
    queryKey: ["/static-translations", language],
    queryFn: () => loadStaticLanguage(language as Language),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    enabled: language !== "en",
  }, client);

  // The server-text catalogues (shared/serverText.ts), in their own chunk, only
  // off English. A changed `data` re-renders every caller once they arrive, so
  // server text already on screen is translated in place.
  const { data: serverTextReady } = useQuery({
    queryKey: ["/server-text-catalogues"],
    queryFn: () => loadServerTextCatalogues().then(() => true),
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: language !== "en",
  }, client);

  return accessorFor({
    serverTextReady: !!serverTextReady,
    language,
    db: language === "en" ? undefined : dbTranslations,
    staticLang: language === "en" ? undefined : staticLangData,
  });
}
